import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return <NotFound />;
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="bg-background-50 border-b border-background-200/70">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-6">
              <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
                <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to="/article" className="hover:text-primary-600 transition-colors whitespace-nowrap">最新资讯</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-900 font-medium line-clamp-1">{article.title}</span>
              </nav>

              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-3">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  阅读 {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.views.toLocaleString()} 阅读
                </span>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <img
              src={article.cover}
              alt={article.title}
              title={`${article.title} - 91在线观看`}
              className="w-full rounded-xl mb-8 object-cover aspect-[16/9]"
            />

            <div className="prose-custom text-foreground-800 leading-relaxed">
              {article.content.split('\n\n').map((paragraph, index) => (
                <p key={index} className="mb-5 text-sm md:text-base leading-relaxed">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500 mr-1">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between p-5 bg-background-100 rounded-xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-thumb-up-line text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">这篇文章对你有帮助吗？</p>
                  <p className="text-xs text-foreground-500">{article.views.toLocaleString()} 人已阅读</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-thumb-up-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-50 text-foreground-500 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-12">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-6">相关推荐</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/50 hover:scale-[1.02] transition-all duration-300"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={rel.cover}
                        alt={rel.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h4>
                      <span className="text-xs text-foreground-500 mt-2 block">{rel.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.summary,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.date,
          image: article.cover,
          publisher: {
            '@type': 'Organization',
            name: '91在线观看',
            url: 'https://www.91zaixianguankan.com/',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://www.91zaixianguankan.com/article/${article.id}`,
          },
        }) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91zaixianguankan.com/' },
            { '@type': 'ListItem', position: 2, name: '最新资讯', item: 'https://www.91zaixianguankan.com/article' },
            { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.91zaixianguankan.com/article/${article.id}` },
          ],
        }) }} />
      </main>
      <Footer />
    </>
  );
}